// src/components/AdminUserList.js
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, limit, startAfter, getDocs, where } from 'firebase/firestore';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import EditUserModal from './EditUserModal';

function AdminUserList() {
  const [users, setUsers] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const USERS_PER_PAGE = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const usersQuery = query(collection(db, 'users'), orderBy('email'), limit(USERS_PER_PAGE));
      const usersSnapshot = await getDocs(usersQuery);
      
      setUsers(usersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setLastDoc(usersSnapshot.docs[usersSnapshot.docs.length - 1]);
      setHasMore(usersSnapshot.docs.length === USERS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users. Check Firestore security rules.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreUsers = async () => {
    if (!lastDoc) return;

    try {
      const usersQuery = query(collection(db, 'users'), orderBy('email'), startAfter(lastDoc), limit(USERS_PER_PAGE));
      const usersSnapshot = await getDocs(usersQuery);

      setUsers((prev) => [...prev, ...usersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))]);
      setLastDoc(usersSnapshot.docs[usersSnapshot.docs.length - 1]);
      setHasMore(usersSnapshot.docs.length === USERS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching more users:', error);
      toast.error('Failed to load more users.');
    }
  };

  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term.');
      return;
    }

    setLoading(true);
    try {
      // Case-insensitive search is tricky in Firestore
      // For email search, we can use direct equality since emails are already stored lowercase
      const usersQuery = query(
        collection(db, 'users'), 
        where('email', '==', searchTerm.toLowerCase()),
        limit(USERS_PER_PAGE)
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      
      setUsers(usersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setLastDoc(null);
      setHasMore(false);
      
      if (usersSnapshot.empty) {
        toast.info('No users found with that email address.');
      }
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users.');
    } finally {
      setLoading(false);
    }
  };

  const resetSearch = () => {
    setSearchTerm('');
    fetchUsers();
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleUpdateUser = (updatedUser) => {
    setUsers(users.map(user => user.id === updatedUser.id ? updatedUser : user));
  };

  const getSubscriptionBadgeClass = (tier) => {
    switch(tier) {
      case 'sixMonth':
        return 'bg-blue-100 text-blue-800';
      case 'lifetime':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle Firestore timestamp
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    
    // Handle regular Date object
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString();
    }
    
    // Handle seconds timestamp
    if (typeof timestamp === 'number') {
      return new Date(timestamp * 1000).toLocaleDateString();
    }
    
    return 'N/A';
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-3xl font-bold mb-6">Users</h2>

      {/* Search bar */}
      <div className="mb-6 flex space-x-2">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by email"
          className="flex-grow border rounded px-3 py-2"
        />
        <button 
          onClick={searchUsers} 
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Search
        </button>
        {searchTerm && (
          <button 
            onClick={resetSearch} 
            className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
          >
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center">
          <ClipLoader size={50} color="#123abc" />
        </div>
      ) : (
        <div className="overflow-x-auto shadow">
          <table className="w-full border-collapse border border-gray-200 bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2 text-left">Email</th>
                <th className="border px-4 py-2 text-left">Role</th>
                <th className="border px-4 py-2 text-left">Subscription</th>
                <th className="border px-4 py-2 text-left">Expiration</th>
                <th className="border px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="border px-4 py-2">{user.email}</td>
                  <td className="border px-4 py-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td className="border px-4 py-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${getSubscriptionBadgeClass(user.subscriptionTier)}`}>
                      {user.subscriptionTier || 'none'}
                    </span>
                  </td>
                  <td className="border px-4 py-2">
                    {formatDate(user.expirationDate)}
                  </td>
                  <td className="border px-4 py-2">
                    <button 
                      onClick={() => handleEditUser(user)} 
                      className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && (
        <button 
          onClick={fetchMoreUsers} 
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Load More
        </button>
      )}

      {showEditModal && selectedUser && (
        <EditUserModal 
          user={selectedUser} 
          onClose={() => setShowEditModal(false)} 
          onUpdate={handleUpdateUser} 
        />
      )}
    </div>
  );
}

export default AdminUserList;
